"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { useLocale, useTranslations } from "next-intl";
import { useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import {
  createContactFormSchema,
  MESSENGERS,
  type ContactFormValues,
} from "@/lib/schemas";

export function ContactForm() {
  const t = useTranslations();
  const locale = useLocale();
  const [sent, setSent] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  // Honeypot: скрытое от людей поле. Заполнено - значит это бот.
  const honeypotRef = useRef<HTMLInputElement>(null);

  const schema = useMemo(
    () =>
      createContactFormSchema({
        nameRequired: t("formErrors.nameRequired"),
        phoneRequired: t("formErrors.phoneRequired"),
        phoneInvalid: t("formErrors.phoneInvalid"),
      }),
    [t],
  );

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<ContactFormValues>({
    resolver: zodResolver(schema),
    defaultValues: { name: "", phone: "", messenger: "WhatsApp" },
  });

  const messenger = watch("messenger");

  const onSubmit = async (data: ContactFormValues) => {
    setSendError(null);
    try {
      const res = await fetch("/api/lead", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...data,
          locale,
          source: "form",
          company: honeypotRef.current?.value ?? "",
        }),
      });
      if (!res.ok) throw new Error(`lead request failed: ${res.status}`);
      setSent(true);
      reset({ name: "", phone: "", messenger: data.messenger });
    } catch (e) {
      // Молчаливый провал здесь = потерянный лид: показываем ошибку и телефон,
      // по которому можно достучаться в обход формы.
      console.error(e);
      setSendError(t("formError"));
    }
  };

  const formAssurances = t.raw("formAssurances") as string[];

  return (
    <section
      className="mx-auto max-w-[1280px] px-[clamp(20px,4vw,40px)] py-[clamp(72px,9vw,120px)]"
    >
      <div className="grid grid-cols-[repeat(auto-fit,minmax(300px,1fr))] items-start gap-[clamp(40px,6vw,88px)]">
        <div data-reveal className="min-w-0">
          <p className="mb-[18px] text-xs uppercase tracking-[.2em] text-accent">
            {t("formKicker")}
          </p>
          <h2 className="max-w-[20ch] text-[clamp(28px,3.4vw,44px)] leading-[1.12]">
            {t("formTitle")}
          </h2>
          <p className="mt-[18px] max-w-[46ch] text-[16px] leading-[1.72] font-light text-muted">
            {t("formSub")}
          </p>
          <div className="mt-8 grid gap-3.5">
            {formAssurances.map((a) => (
              <div key={a} className="flex items-start gap-3 text-[14px] leading-[1.5] text-muted">
                <span
                  aria-hidden
                  className="mt-[7px] h-1.5 w-1.5 flex-none rounded-full bg-accent"
                />
                <span>{a}</span>
              </div>
            ))}
          </div>
        </div>

        <form
          onSubmit={handleSubmit(onSubmit)}
          noValidate
          className="grid min-w-0 gap-[18px] rounded border border-ink/10 bg-white p-[clamp(24px,3vw,36px)]"
        >
          <label className="grid gap-2">
            <span className="text-xs tracking-[.1em] text-muted uppercase">
              {t("fieldName")}
            </span>
            <input
              type="text"
              placeholder={t("fieldNamePh")}
              aria-invalid={!!errors.name}
              className="w-full rounded-[3px] border border-ink/[.14] bg-bg px-3.5 py-[13px] text-[15px] outline-none transition-colors focus:border-accent"
              {...register("name")}
            />
            {errors.name ? (
              <span className="text-[12.5px] text-red-600">{errors.name.message}</span>
            ) : null}
          </label>

          <label className="grid gap-2">
            <span className="text-xs tracking-[.1em] text-muted uppercase">
              {t("fieldPhone")}
            </span>
            <input
              type="tel"
              placeholder="+66 __ ___ ____"
              aria-invalid={!!errors.phone}
              className="w-full rounded-[3px] border border-ink/[.14] bg-bg px-3.5 py-[13px] text-[15px] outline-none transition-colors focus:border-accent"
              {...register("phone")}
            />
            {errors.phone ? (
              <span className="text-[12.5px] text-red-600">{errors.phone.message}</span>
            ) : null}
          </label>

          <div className="grid gap-2">
            <span className="text-xs tracking-[.1em] text-muted uppercase">
              {t("fieldMessenger")}
            </span>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              {MESSENGERS.map((m) => {
                const active = messenger === m;
                return (
                  <button
                    key={m}
                    type="button"
                    onClick={() => setValue("messenger", m)}
                    aria-pressed={active}
                    className="rounded-[3px] border px-4 py-3 text-[13.5px] font-medium transition-colors"
                    style={{
                      borderColor: active ? "var(--color-accent)" : "rgba(26,29,33,.16)",
                      background: active ? "var(--color-accent)" : "#fff",
                      color: active ? "#fff" : "#5A626B",
                    }}
                  >
                    {m}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Honeypot: скрыт от людей и от скринридеров, боты его заполняют. */}
          <input
            ref={honeypotRef}
            type="text"
            name="company"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            className="absolute left-[-9999px] h-0 w-0 opacity-0"
          />

          <button
            type="submit"
            disabled={isSubmitting}
            className="mt-1.5 rounded-[3px] border-0 bg-accent px-5 py-4 text-[14.5px] font-medium text-white transition-colors hover:bg-ink disabled:opacity-60"
          >
            {sent ? t("submitted") : t("submit")}
          </button>
          {sendError ? (
            <p role="alert" className="text-[13px] leading-[1.5] text-red-600">
              {sendError}
            </p>
          ) : null}
          <p aria-live="polite" className="sr-only">
            {sent ? t("submitted") : ""}
          </p>
          <p className="text-[12px] leading-[1.6] text-subtle">{t("consent")}</p>
        </form>
      </div>
    </section>
  );
}
